import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchPublicCatalog } from '../../lib/api/public-catalog.api';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  limit,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { admissionsApi } from '../../lib/api/admissions.api';
import { isModuleEnabled, type TenantConfig } from '@dosfilos/domain';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../lib/error-text';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantSnapshot {
  id: string;
  name: string;
  slug: string;
  config?: TenantConfig;
}

interface ProgramDocRequirement {
  type: string;
  required: boolean;
  description?: string;
}

interface ProgramSnapshot {
  id: string;
  name: string;
  description?: string;
  formTemplateId?: string;
  documentRequirements?: ProgramDocRequirement[];
}

type FormFieldType = 'text' | 'textarea' | 'select' | 'date' | 'file' | 'checkbox';

interface FormFieldDefinition {
  id: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
}

interface FormTemplateSnapshot {
  id: string;
  name: string;
  welcomeMessage?: string | undefined;
  confirmationMessage?: string | undefined;
  fields: FormFieldDefinition[];
}

/** Core applicant identity — always collected, always the same */
interface IdentityData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  church: string;
}

type Step = 'program' | 'identity' | 'template' | 'confirm' | 'success';

// ─── Design tokens ────────────────────────────────────────────────────────────

const DARK = '#0d0f0f';
const SURFACE = '#141618';
const BORDER = 'rgba(255,255,255,0.08)';
const TEXT = '#f0ece4';
const TEXT_MUTED = '#a09880';

function muteColor(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const mix = (v: number) => Math.round(v * 0.55 + 180 * 0.45);
  return `#${mix(r).toString(16).padStart(2, '0')}${mix(g).toString(16).padStart(2, '0')}${mix(b).toString(16).padStart(2, '0')}`;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${BORDER}`,
  borderRadius: 6,
  color: TEXT,
  fontSize: 15,
  padding: '12px 14px',
  outline: 'none',
  fontFamily: 'Inter, sans-serif',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEXT_MUTED,
  marginBottom: 6,
  fontFamily: 'Inter, sans-serif',
};

// ─── Field Components ─────────────────────────────────────────────────────────

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  as?: 'textarea' | undefined;
  helpText?: string | undefined;
  gold: string;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  as,
  helpText,
  gold,
}: FieldProps) {
  const [focused, setFocused] = useState(false);
  const focusBorder = focused ? gold : BORDER;
  const fieldStyle = {
    ...inputStyle,
    borderColor: focusBorder,
    ...(as === 'textarea' ? { minHeight: 110, resize: 'vertical' as const } : {}),
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <label htmlFor={id} style={labelStyle}>
        {label} {required && <span style={{ color: gold }}>*</span>}
      </label>
      {as === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={fieldStyle}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={fieldStyle}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      )}
      {helpText && (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            color: TEXT_MUTED,
            marginTop: 5,
            lineHeight: 1.5,
          }}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  required?: boolean | undefined;
  helpText?: string | undefined;
  gold: string;
}

function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  required,
  helpText,
  gold,
}: SelectFieldProps) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 20 }}>
      <label htmlFor={id} style={labelStyle}>
        {label} {required && <span style={{ color: gold }}>*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, borderColor: focused ? gold : BORDER, cursor: 'pointer' }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <option value="">— Selecciona una opción —</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {helpText && (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            color: TEXT_MUTED,
            marginTop: 5,
            lineHeight: 1.5,
          }}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}

interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  helpText?: string | undefined;
  gold: string;
}

function CheckboxField({ id, label, checked, onChange, helpText, gold }: CheckboxFieldProps) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor={id}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
      >
        <div
          onClick={() => onChange(!checked)}
          style={{
            width: 18,
            height: 18,
            borderRadius: 4,
            flexShrink: 0,
            marginTop: 1,
            border: `2px solid ${checked ? gold : BORDER}`,
            background: checked ? gold : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          {checked && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1.5 5.5l2.5 2.5 4.5-5"
                stroke={DARK}
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>
        <span
          style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT, lineHeight: 1.5 }}
        >
          {label}
        </span>
      </label>
      {helpText && (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            color: TEXT_MUTED,
            marginTop: 5,
            lineHeight: 1.5,
            paddingLeft: 28,
          }}
        >
          {helpText}
        </p>
      )}
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={() => onChange(!checked)}
        style={{ display: 'none' }}
      />
    </div>
  );
}

// ─── File Upload Field ────────────────────────────────────────────────────────

interface FileUploadFieldProps {
  id: string;
  label: string;
  /** Download URL stored as answer once upload completes */
  value: string;
  onChange: (downloadUrl: string) => void;
  required?: boolean | undefined;
  helpText?: string | undefined;
  gold: string;
  tenantId: string;
}

const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/png,image/webp';
const MAX_MB = 10;

function FileUploadField({
  id,
  label,
  value,
  onChange,
  required,
  helpText,
  gold,
  tenantId,
}: FileUploadFieldProps) {
  const [progress, setProgress] = useState<number | null>(null); // 0-100 or null
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [filename, setFilename] = useState<string>('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const gr = parseInt(gold.slice(1, 3), 16);
  const gg = parseInt(gold.slice(3, 5), 16);
  const gb = parseInt(gold.slice(5, 7), 16);

  const upload = (file: File) => {
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`El archivo no puede superar ${MAX_MB} MB.`);
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setUploadError('Solo se aceptan PDF e imágenes (JPG, PNG, WEBP).');
      return;
    }

    setUploadError(null);
    setFilename(file.name);
    setProgress(0);

    const storage = getStorage();
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(
      storage,
      `tenants/${tenantId}/public-admission-uploads/${timestamp}_${safeName}`,
    );
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });

    task.on(
      'state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error('[FileUploadField] upload error:', err);
        setUploadError('Error al subir el archivo. Inténtalo de nuevo.');
        setProgress(null);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onChange(url);
        setProgress(100);
      },
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const uploaded = !!value && progress === 100;

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={labelStyle}>
        {label} {required && <span style={{ color: gold }}>*</span>}
      </label>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${uploaded ? gold : dragging ? gold : BORDER}`,
          borderRadius: 8,
          padding: '24px 20px',
          textAlign: 'center',
          cursor: progress !== null && progress < 100 ? 'default' : 'pointer',
          background: dragging
            ? `rgba(${gr},${gg},${gb},0.06)`
            : uploaded
              ? `rgba(${gr},${gg},${gb},0.05)`
              : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
        }}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* Icon */}
        {uploaded ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `rgba(${gr},${gg},${gb},0.15)`,
                border: `2px solid ${gold}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M3 9.5l4 4 8-8"
                  stroke={gold}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: gold,
                margin: 0,
                fontWeight: 600,
              }}
            >
              {filename}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setProgress(null);
                setFilename('');
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                color: TEXT_MUTED,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Cambiar archivo
            </button>
          </div>
        ) : progress !== null && progress < 100 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: TEXT_MUTED,
                margin: 0,
              }}
            >
              Subiendo <strong style={{ color: TEXT }}>{filename}</strong>…
            </p>
            {/* Progress bar */}
            <div
              style={{
                width: '100%',
                maxWidth: 280,
                height: 4,
                background: BORDER,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: gold,
                  borderRadius: 2,
                  transition: 'width 0.2s',
                }}
              />
            </div>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                color: TEXT_MUTED,
                margin: 0,
              }}
            >
              {progress}%
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BORDER}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M9 11V4M9 4L6 7M9 4l3 3"
                  stroke={TEXT_MUTED}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M3 14h12" stroke={TEXT_MUTED} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                color: TEXT_MUTED,
                margin: 0,
              }}
            >
              <span style={{ color: gold, fontWeight: 600 }}>Haz clic para seleccionar</span> o
              arrastra aquí
            </p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#555', margin: 0 }}>
              PDF, JPG, PNG, WEBP — máx. {MAX_MB} MB
            </p>
          </div>
        )}
      </div>

      {uploadError && (
        <p
          style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#e57373', marginTop: 6 }}
        >
          {uploadError}
        </p>
      )}
      {helpText && !uploadError && (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            color: TEXT_MUTED,
            marginTop: 5,
            lineHeight: 1.5,
          }}
        >
          {helpText}
        </p>
      )}
    </div>
  );
}

/** Renders a single FormFieldDefinition dynamically */
function DynamicField({
  field,
  value,
  onChange,
  gold,
  tenantId,
}: {
  field: FormFieldDefinition;
  value: string;
  onChange: (v: string) => void;
  gold: string;
  tenantId: string;
}) {
  switch (field.type) {
    case 'textarea':
      return (
        <Field
          id={field.id}
          label={field.label}
          value={value}
          onChange={onChange}
          as="textarea"
          placeholder={field.placeholder}
          required={field.required}
          helpText={field.helpText}
          gold={gold}
        />
      );
    case 'select':
      return (
        <SelectField
          id={field.id}
          label={field.label}
          value={value}
          onChange={onChange}
          options={field.options ?? []}
          required={field.required}
          helpText={field.helpText}
          gold={gold}
        />
      );
    case 'checkbox':
      return (
        <CheckboxField
          id={field.id}
          label={field.label}
          checked={value === 'true'}
          onChange={(v) => onChange(v ? 'true' : 'false')}
          helpText={field.helpText}
          gold={gold}
        />
      );
    case 'file':
      return (
        <FileUploadField
          id={field.id}
          label={field.label}
          value={value}
          onChange={onChange}
          required={field.required}
          helpText={field.helpText}
          gold={gold}
          tenantId={tenantId}
        />
      );
    default: // text | date
      return (
        <Field
          id={field.id}
          label={field.label}
          value={value}
          onChange={onChange}
          type={field.type}
          placeholder={field.placeholder}
          required={field.required}
          helpText={field.helpText}
          gold={gold}
        />
      );
  }
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({
  current,
  hasTemplate,
  gold,
}: {
  current: Step;
  hasTemplate: boolean;
  gold: string;
}) {
  const steps: { key: Step; label: string }[] = hasTemplate
    ? [
        { key: 'program', label: 'Programa' },
        { key: 'identity', label: 'Datos' },
        { key: 'template', label: 'Formulario' },
        { key: 'confirm', label: 'Confirmar' },
      ]
    : [
        { key: 'program', label: 'Programa' },
        { key: 'identity', label: 'Datos' },
        { key: 'confirm', label: 'Confirmar' },
      ];

  const allSteps: Step[] = ['program', 'identity', 'template', 'confirm', 'success'];
  const currentIdx = allSteps.indexOf(current);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        marginBottom: 40,
      }}
    >
      {steps.map((s, i) => {
        const idx = allSteps.indexOf(s.key);
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && (
              <div
                style={{
                  width: 40,
                  height: 1,
                  background: done ? gold : BORDER,
                  transition: 'background 0.3s',
                }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: `2px solid ${active || done ? gold : BORDER}`,
                  background: done ? gold : active ? 'rgba(180,140,80,0.12)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s',
                }}
              >
                {done ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2.5 7.5l3 3 6-6"
                      stroke={DARK}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: active ? gold : TEXT_MUTED }}
                  >
                    {i + 1}
                  </span>
                )}
              </div>
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: active ? gold : done ? TEXT_MUTED : '#555',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                }}
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ApplyPublicPage() {
  const { t } = useTranslation('sis');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // `?program=<id>` llega desde la página pública de un programa («Postular a
  // este programa»): se marca solo, y el postulante puede cambiarlo.
  const [searchParams] = useSearchParams();
  const preselectedProgramId = searchParams.get('program');

  // ── Remote data state ──────────────────────────────────────────────────────
  const [tenant, setTenant] = useState<TenantSnapshot | null>(null);
  const [programs, setPrograms] = useState<ProgramSnapshot[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // ── Template state ─────────────────────────────────────────────────────────
  const [formTemplate, setFormTemplate] = useState<FormTemplateSnapshot | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('program');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [selectedProgramId, setSelectedProgramId] = useState('');
  // Uploaded documents keyed by requirement type → download URL.
  const [docUploads, setDocUploads] = useState<Record<string, string>>({});

  const [identity, setIdentity] = useState<IdentityData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    church: '',
  });

  /** Answers keyed by FormFieldDefinition.id */
  const [templateAnswers, setTemplateAnswers] = useState<Record<string, string>>({});

  // ── Load tenant + programs ─────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setPageLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'tenants'), where('slug', '==', slug), limit(1)),
        );
        if (snap.empty) {
          if (!cancelled) {
            setNotFound(true);
            setPageLoading(false);
          }
          return;
        }

        const [tenantDoc] = snap.docs;
        if (!tenantDoc) {
          if (!cancelled) {
            setNotFound(true);
            setPageLoading(false);
          }
          return;
        }

        const data = tenantDoc.data() as DocumentData;
        const cfg = data['config'] as TenantConfig | undefined;
        const resolved: TenantSnapshot = {
          id: tenantDoc.id,
          name: data['name'] as string,
          slug: data['slug'] as string,
          ...(cfg !== undefined ? { config: cfg } : {}),
        };

        if (cancelled) return;
        setTenant(resolved);

        // Los programas salen del catálogo público (`getPublicCatalog`): el
        // documento del programa trae datos internos y no es legible sin sesión.
        // Si el catálogo falla, la lista queda vacía (no "seminario no encontrado").
        const catalog = await fetchPublicCatalog(resolved.slug).catch((err: unknown) => {
          console.error('[ApplyPublicPage] catalog failed:', err);
          return null;
        });
        if (!cancelled) {
          setPrograms(
            (catalog?.programs ?? [])
              .map(
                (p): ProgramSnapshot => ({
                  id: p.id,
                  name: p.name,
                  ...(p.description ? { description: p.description } : {}),
                  ...(p.formTemplateId ? { formTemplateId: p.formTemplateId } : {}),
                  // La página usa `type` como clave y etiqueta del documento.
                  documentRequirements: p.documents.map((d) => ({
                    type: d.label,
                    required: d.required,
                    ...(d.description ? { description: d.description } : {}),
                  })),
                }),
              )
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
          setPageLoading(false);
        }
      } catch (err) {
        console.error('[ApplyPublicPage] failed:', err);
        if (!cancelled) {
          setNotFound(true);
          setPageLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ── Load form template when a program is selected ─────────────────────────
  const loadTemplate = useCallback(
    async (programId: string, tenantId: string) => {
      const program = programs.find((p) => p.id === programId);
      if (!program?.formTemplateId) {
        setFormTemplate(null);
        return;
      }
      setTemplateLoading(true);
      try {
        const templateDoc = await getDoc(
          doc(db, `tenants/${tenantId}/formTemplates/${program.formTemplateId}`),
        );
        if (templateDoc.exists()) {
          const d = templateDoc.data() as DocumentData;
          setFormTemplate({
            id: templateDoc.id,
            name: d['name'] as string,
            welcomeMessage: d['welcomeMessage'] as string | undefined,
            confirmationMessage: d['confirmationMessage'] as string | undefined,
            fields: (d['fields'] as FormFieldDefinition[] | undefined) ?? [],
          });
          // Reset answers when template changes
          setTemplateAnswers({});
        } else {
          setFormTemplate(null);
        }
      } catch (err) {
        console.error('[ApplyPublicPage] failed to load template:', err);
        setFormTemplate(null);
      } finally {
        setTemplateLoading(false);
      }
    },
    [programs],
  );

  // Preselección: una sola vez, cuando ya cargaron los programas, y solo si el
  // id corresponde a un programa activo de este seminario.
  const preselectDone = useRef(false);
  useEffect(() => {
    if (preselectDone.current || !preselectedProgramId || !tenant || programs.length === 0) return;
    preselectDone.current = true;
    if (!programs.some((p) => p.id === preselectedProgramId)) return;
    setSelectedProgramId(preselectedProgramId);
    void loadTemplate(preselectedProgramId, tenant.id);
  }, [preselectedProgramId, tenant, programs, loadTemplate]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const gold = tenant?.config?.primaryColor ? muteColor(tenant.config.primaryColor) : '#b08d57';
  const logoUrl = tenant?.config?.logoUrl;
  const selectedProgram = programs.find((p) => p.id === selectedProgramId);
  const hasTemplate = !!(formTemplate && formTemplate.fields.length > 0);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goBack = useCallback(() => {
    if (step === 'identity') setStep('program');
    else if (step === 'template') setStep('identity');
    else if (step === 'confirm') setStep(hasTemplate ? 'template' : 'identity');
    else navigate(`/s/${slug}`);
  }, [step, hasTemplate, slug, navigate]);

  const canGoNext = useCallback((): boolean => {
    if (step === 'program') return !!selectedProgramId;
    if (step === 'identity') {
      const { firstName, lastName, email } = identity;
      return !!(
        firstName.trim() &&
        lastName.trim() &&
        email.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      );
    }
    if (step === 'template' && formTemplate) {
      return formTemplate.fields
        .filter((f) => f.required)
        .every(
          (f) => (templateAnswers[f.id] ?? '').trim() !== '' && templateAnswers[f.id] !== 'false',
        );
    }
    if (step === 'confirm') {
      // Required documents must be uploaded before submitting.
      return (selectedProgram?.documentRequirements ?? [])
        .filter((r) => r.required)
        .every((r) => !!docUploads[r.type]);
    }
    return true;
  }, [
    step,
    selectedProgramId,
    identity,
    formTemplate,
    templateAnswers,
    selectedProgram,
    docUploads,
  ]);

  const next = useCallback(() => {
    if (step === 'program') setStep('identity');
    else if (step === 'identity') setStep(hasTemplate ? 'template' : 'confirm');
    else if (step === 'template') setStep('confirm');
  }, [step, hasTemplate]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!tenant) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Build applicantFormData: template answers + metadata
      const applicantFormData: Record<string, string> = { ...templateAnswers };
      if (formTemplate) applicantFormData['_templateId'] = formTemplate.id;

      await admissionsApi.publicSubmit({
        tenantId: tenant.id,
        programId: selectedProgramId,
        firstName: identity.firstName.trim(),
        lastName: identity.lastName.trim(),
        email: identity.email.trim(),
        ...(identity.phone.trim() ? { phone: identity.phone.trim() } : {}),
        ...(identity.church.trim() ? { church: identity.church.trim() } : {}),
        ...(Object.keys(applicantFormData).length > 0 ? { applicantFormData } : {}),
        ...(Object.keys(docUploads).length > 0
          ? {
              documents: Object.entries(docUploads).map(([type, url]) => ({
                type,
                storagePath: url,
              })),
            }
          : {}),
      });
      setStep('success');
    } catch (err: unknown) {
      console.error('[ApplyPublicPage] submit failed:', err);
      const msg = errorText(err, 'Ocurrió un error. Por favor intenta de nuevo.');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [tenant, selectedProgramId, identity, templateAnswers, formTemplate, docUploads]);

  // ── Card style ─────────────────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: 40,
    maxWidth: 600,
    width: '100%',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  };

  // ── Loading / Not found guards ─────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: DARK,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: `3px solid ${BORDER}`,
            borderTopColor: '#b08d57',
            animation: 'spin 0.9s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: DARK,
          gap: 16,
        }}
      >
        <p style={{ color: TEXT_MUTED, fontFamily: 'Inter, sans-serif', fontSize: 15 }}>
          Seminario no encontrado
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent',
            border: `1px solid ${BORDER}`,
            borderRadius: 6,
            color: TEXT_MUTED,
            padding: '8px 20px',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  // ADMISIONES APAGADO (Configuración → Módulos): las postulaciones están
  // cerradas. El servidor rechaza igual una postulación nueva; aquí se dice
  // antes de que alguien llene el formulario.
  if (tenant && !isModuleEnabled(tenant.config?.modules, 'admissions')) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: DARK,
          gap: 12,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <h1 style={{ color: TEXT, fontFamily: 'Inter, sans-serif', fontSize: 22, margin: 0 }}>
          {t('publicLanding.applyClosed.title')}
        </h1>
        <p
          style={{
            color: TEXT_MUTED,
            fontFamily: 'Inter, sans-serif',
            fontSize: 15,
            maxWidth: 520,
          }}
        >
          {t('publicLanding.applyClosed.body', { name: tenant.name })}
        </p>
        <button
          onClick={() => navigate(`/s/${tenant.slug}`)}
          style={{
            background: 'transparent',
            border: `1px solid ${BORDER}`,
            borderRadius: 6,
            color: TEXT_MUTED,
            padding: '8px 20px',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {t('publicLanding.applyClosed.back')}
        </button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: DARK, display: 'flex', flexDirection: 'column' }}>
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Inter:wght@300;400;500;600;700&display=swap');
                * { box-sizing: border-box; }
                input::placeholder, textarea::placeholder { color: #555; }
                input:focus, textarea:focus, select:focus { border-color: ${gold} !important; }
                select option { background: #1a1c1e; color: #f0ece4; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

      {/* ── Navbar ── */}
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(13,15,15,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${BORDER}`,
          padding: '0 24px',
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={() => navigate(`/s/${slug}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              style={{ height: 28, width: 28, borderRadius: 4, objectFit: 'contain' }}
            />
          )}
          <span
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 17,
              fontWeight: 600,
              color: TEXT,
              letterSpacing: '0.02em',
            }}
          >
            {tenant?.name}
          </span>
        </button>
        <span
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: TEXT_MUTED,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Formulario de Admisión
        </span>
      </nav>

      {/* ── Main ── */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '48px 16px 80px',
        }}
      >
        {step !== 'success' && (
          <div style={{ textAlign: 'center', marginBottom: 40, animation: 'fadeUp 0.5s ease' }}>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: gold,
                marginBottom: 12,
              }}
            >
              Proceso de Admisión
            </p>
            <h1
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 36,
                fontWeight: 600,
                color: TEXT,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {tenant?.name}
            </h1>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                color: TEXT_MUTED,
                marginTop: 8,
              }}
            >
              Complete el formulario para iniciar tu proceso de admisión
            </p>
          </div>
        )}

        {step !== 'success' && (
          <StepIndicator current={step} hasTemplate={hasTemplate} gold={gold} />
        )}

        <div style={{ ...cardStyle, animation: 'fadeUp 0.5s ease both' }}>
          {/* ══ Step 1: Program selection ══ */}
          {step === 'program' && (
            <div>
              <h2
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 24,
                  fontWeight: 600,
                  color: TEXT,
                  margin: '0 0 6px',
                }}
              >
                Selecciona un Programa
              </h2>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  color: TEXT_MUTED,
                  margin: '0 0 28px',
                }}
              >
                Escoge el programa de estudio al que deseas aplicar.
              </p>

              {programs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <p style={{ color: TEXT_MUTED, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                    No hay programas disponibles en este momento.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {programs.map((p) => {
                    const selected = p.id === selectedProgramId;
                    const gr = parseInt(gold.slice(1, 3), 16);
                    const gg = parseInt(gold.slice(3, 5), 16);
                    const gb = parseInt(gold.slice(5, 7), 16);
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          setSelectedProgramId(p.id);
                          if (tenant) void loadTemplate(p.id, tenant.id);
                        }}
                        style={{
                          background: selected
                            ? `rgba(${gr},${gg},${gb},0.08)`
                            : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${selected ? gold : BORDER}`,
                          borderRadius: 8,
                          padding: '16px 18px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              flexShrink: 0,
                              border: `2px solid ${selected ? gold : BORDER}`,
                              background: selected ? gold : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {selected && (
                              <div
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: DARK,
                                }}
                              />
                            )}
                          </div>
                          <div>
                            <p
                              style={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: 14,
                                fontWeight: 600,
                                color: selected ? gold : TEXT,
                                margin: 0,
                              }}
                            >
                              {p.name}
                            </p>
                            {p.description && (
                              <p
                                style={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: 12,
                                  color: TEXT_MUTED,
                                  margin: '4px 0 0',
                                  lineHeight: 1.5,
                                }}
                              >
                                {p.description}
                              </p>
                            )}
                            {p.formTemplateId && (
                              <p
                                style={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: 11,
                                  color: selected
                                    ? `rgba(${parseInt(gold.slice(1, 3), 16)},${parseInt(gold.slice(3, 5), 16)},${parseInt(gold.slice(5, 7), 16)},0.7)`
                                    : '#555',
                                  margin: '6px 0 0',
                                  letterSpacing: '0.05em',
                                }}
                              >
                                ✦ Incluye formulario de admisión personalizado
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══ Step 2: Identity data ══ */}
          {step === 'identity' && (
            <div>
              <h2
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 24,
                  fontWeight: 600,
                  color: TEXT,
                  margin: '0 0 6px',
                }}
              >
                Información Personal
              </h2>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  color: TEXT_MUTED,
                  margin: '0 0 28px',
                }}
              >
                Tus datos de contacto para el proceso de admisión.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Field
                  id="firstName"
                  label="Nombre"
                  value={identity.firstName}
                  onChange={(v) => setIdentity((d) => ({ ...d, firstName: v }))}
                  required
                  gold={gold}
                />
                <Field
                  id="lastName"
                  label="Apellido"
                  value={identity.lastName}
                  onChange={(v) => setIdentity((d) => ({ ...d, lastName: v }))}
                  required
                  gold={gold}
                />
              </div>
              <Field
                id="email"
                label="Correo electrónico"
                type="email"
                value={identity.email}
                onChange={(v) => setIdentity((d) => ({ ...d, email: v }))}
                placeholder="nombre@correo.com"
                required
                gold={gold}
              />
              <Field
                id="phone"
                label="Teléfono (opcional)"
                type="tel"
                value={identity.phone}
                onChange={(v) => setIdentity((d) => ({ ...d, phone: v }))}
                placeholder="+1 000 000 0000"
                gold={gold}
              />
              <Field
                id="church"
                label="Organización / Iglesia (opcional)"
                value={identity.church}
                onChange={(v) => setIdentity((d) => ({ ...d, church: v }))}
                placeholder="Nombre de tu iglesia u organización"
                gold={gold}
              />
            </div>
          )}

          {/* ══ Step 3: Dynamic template fields ══ */}
          {step === 'template' && formTemplate && (
            <div>
              {templateLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: `2px solid ${BORDER}`,
                      borderTopColor: gold,
                      animation: 'spin 0.9s linear infinite',
                      margin: '0 auto',
                    }}
                  />
                </div>
              ) : (
                <>
                  <h2
                    style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: 24,
                      fontWeight: 600,
                      color: TEXT,
                      margin: '0 0 6px',
                    }}
                  >
                    {formTemplate.name}
                  </h2>
                  {formTemplate.welcomeMessage ? (
                    <p
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 13,
                        color: TEXT_MUTED,
                        margin: '0 0 28px',
                        lineHeight: 1.6,
                      }}
                    >
                      {formTemplate.welcomeMessage}
                    </p>
                  ) : (
                    <p
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 13,
                        color: TEXT_MUTED,
                        margin: '0 0 28px',
                      }}
                    >
                      Completa las preguntas del programa{' '}
                      <strong style={{ color: TEXT }}>{selectedProgram?.name}</strong>.
                    </p>
                  )}

                  {formTemplate.fields.map((field) => (
                    <DynamicField
                      key={field.id}
                      field={field}
                      value={templateAnswers[field.id] ?? ''}
                      onChange={(v) => setTemplateAnswers((prev) => ({ ...prev, [field.id]: v }))}
                      gold={gold}
                      tenantId={tenant?.id ?? ''}
                    />
                  ))}
                </>
              )}
            </div>
          )}

          {/* ══ Step 4: Confirm ══ */}
          {step === 'confirm' && (
            <div>
              <h2
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 24,
                  fontWeight: 600,
                  color: TEXT,
                  margin: '0 0 6px',
                }}
              >
                Confirma tu Solicitud
              </h2>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 13,
                  color: TEXT_MUTED,
                  margin: '0 0 24px',
                }}
              >
                Revisa la información antes de enviar.
              </p>

              {/* Required / optional documents */}
              {(selectedProgram?.documentRequirements ?? []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: gold,
                      margin: '0 0 12px',
                    }}
                  >
                    Documentos
                  </p>
                  {(selectedProgram?.documentRequirements ?? []).map((r) => (
                    <FileUploadField
                      key={r.type}
                      id={`doc-${r.type}`}
                      label={r.type}
                      value={docUploads[r.type] ?? ''}
                      onChange={(url) => setDocUploads((prev) => ({ ...prev, [r.type]: url }))}
                      required={r.required}
                      {...(r.description ? { helpText: r.description } : {})}
                      gold={gold}
                      tenantId={tenant?.id ?? ''}
                    />
                  ))}
                </div>
              )}

              {/* Identity summary */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                  padding: 20,
                  marginBottom: 16,
                }}
              >
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: gold,
                    margin: '0 0 12px',
                  }}
                >
                  Datos personales
                </p>
                {[
                  { label: 'Programa', value: selectedProgram?.name ?? '—' },
                  { label: 'Nombre', value: `${identity.firstName} ${identity.lastName}` },
                  { label: 'Correo', value: identity.email },
                  ...(identity.phone ? [{ label: 'Teléfono', value: identity.phone }] : []),
                  ...(identity.church
                    ? [{ label: 'Organización / Iglesia', value: identity.church }]
                    : []),
                ].map((row, i, arr) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '8px 0',
                      borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : 'none',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: TEXT_MUTED,
                        minWidth: 88,
                      }}
                    >
                      {row.label}
                    </span>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: TEXT }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Template answers summary */}
              {hasTemplate && formTemplate && formTemplate.fields.length > 0 && (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding: 20,
                    marginBottom: 16,
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: gold,
                      margin: '0 0 12px',
                    }}
                  >
                    {formTemplate.name}
                  </p>
                  {formTemplate.fields.map((field, i) => {
                    const val = templateAnswers[field.id];
                    if (!val) return null;
                    const isUrl = /^https?:\/\//i.test(val);
                    let valueNode: React.ReactNode;
                    if (isUrl) {
                      valueNode = (
                        <a
                          href={val}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 13,
                            color: gold,
                            textDecoration: 'none',
                            borderBottom: `1px dashed ${gold}66`,
                            paddingBottom: 1,
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                          Ver documento
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="7 17 17 7" />
                            <polyline points="7 7 17 7 17 17" />
                          </svg>
                        </a>
                      );
                    } else {
                      const displayVal =
                        field.type === 'checkbox'
                          ? val === 'true'
                            ? '✓ Sí'
                            : '✗ No'
                          : val.length > 120
                            ? val.slice(0, 120) + '…'
                            : val;
                      valueNode = (
                        <span
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 14,
                            color: TEXT,
                            lineHeight: 1.5,
                          }}
                        >
                          {displayVal}
                        </span>
                      );
                    }
                    return (
                      <div
                        key={field.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '8px 0',
                          borderBottom:
                            i < formTemplate.fields.length - 1 ? `1px solid ${BORDER}` : 'none',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: TEXT_MUTED,
                            minWidth: 130,
                            flexShrink: 0,
                          }}
                        >
                          {field.label}
                        </span>
                        {valueNode}
                      </div>
                    );
                  })}
                </div>
              )}

              {submitError && (
                <div
                  style={{
                    background: 'rgba(220,60,60,0.08)',
                    border: '1px solid rgba(220,60,60,0.3)',
                    borderRadius: 6,
                    padding: '12px 16px',
                    marginBottom: 20,
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 13,
                      color: '#e57373',
                      margin: 0,
                    }}
                  >
                    {submitError}
                  </p>
                </div>
              )}

              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  color: TEXT_MUTED,
                  margin: '0 0 4px',
                  lineHeight: 1.6,
                }}
              >
                Al enviar tu solicitud, el seminario recibirá tus datos para evaluar tu admisión. Te
                contactarán pronto con los próximos pasos.
              </p>
            </div>
          )}

          {/* ══ Step 5: Success ══ */}
          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: `rgba(${parseInt(gold.slice(1, 3), 16)},${parseInt(gold.slice(3, 5), 16)},${parseInt(gold.slice(5, 7), 16)},0.12)`,
                  border: `2px solid ${gold}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path
                    d="M5 15l7 7L23 7"
                    stroke={gold}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 30,
                  fontWeight: 600,
                  color: TEXT,
                  margin: '0 0 12px',
                }}
              >
                ¡Solicitud Enviada!
              </h2>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  color: TEXT_MUTED,
                  margin: '0 0 8px',
                  lineHeight: 1.7,
                }}
              >
                Hemos recibido tu solicitud de admisión para{' '}
                <strong style={{ color: TEXT }}>{selectedProgram?.name}</strong> en{' '}
                <strong style={{ color: TEXT }}>{tenant?.name}</strong>.
              </p>
              {formTemplate?.confirmationMessage && (
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 13,
                    color: TEXT_MUTED,
                    margin: '0 0 28px',
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                  }}
                >
                  {formTemplate.confirmationMessage}
                </p>
              )}
              {!formTemplate?.confirmationMessage && (
                <p
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 13,
                    color: TEXT_MUTED,
                    margin: '0 0 28px',
                    lineHeight: 1.7,
                  }}
                >
                  El equipo del seminario revisará tu solicitud y se pondrá en contacto contigo
                  pronto.
                </p>
              )}
              <button
                onClick={() => navigate(`/s/${slug}`)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${gold}`,
                  borderRadius: 6,
                  color: gold,
                  padding: '12px 28px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.background =
                    `rgba(${parseInt(gold.slice(1, 3), 16)},${parseInt(gold.slice(3, 5), 16)},${parseInt(gold.slice(5, 7), 16)},0.1)`;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                Volver al inicio
              </button>
            </div>
          )}

          {/* ── Footer buttons ── */}
          {step !== 'success' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 32,
                paddingTop: 24,
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <button
                onClick={goBack}
                style={{
                  background: 'transparent',
                  border: `1px solid ${BORDER}`,
                  borderRadius: 6,
                  color: TEXT_MUTED,
                  padding: '11px 22px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  letterSpacing: '0.02em',
                  transition: 'all 0.2s',
                }}
              >
                {step === 'program' ? 'Cancelar' : 'Atrás'}
              </button>

              {step !== 'confirm' ? (
                <button
                  id="next-btn"
                  onClick={next}
                  disabled={!canGoNext() || templateLoading}
                  style={{
                    background: canGoNext() && !templateLoading ? gold : 'rgba(255,255,255,0.06)',
                    border: 'none',
                    borderRadius: 6,
                    color: canGoNext() && !templateLoading ? DARK : '#555',
                    padding: '12px 28px',
                    cursor: canGoNext() && !templateLoading ? 'pointer' : 'default',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    transition: 'all 0.25s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  {templateLoading && step === 'program' && (
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: `2px solid rgba(0,0,0,0.2)`,
                        borderTopColor: DARK,
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                  )}
                  Continuar
                </button>
              ) : (
                <button
                  id="submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    background: submitting ? 'rgba(255,255,255,0.06)' : gold,
                    border: 'none',
                    borderRadius: 6,
                    color: submitting ? '#555' : DARK,
                    padding: '12px 28px',
                    cursor: submitting ? 'default' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    transition: 'all 0.25s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  {submitting && (
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid rgba(0,0,0,0.2)`,
                        borderTopColor: DARK,
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                  )}
                  {submitting ? 'Enviando…' : 'Enviar Solicitud'}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
